import React, { useEffect, useRef } from 'react';
import useState from 'react-usestateref'
import { toast } from 'react-toastify';
import { InfinitySpin } from 'react-loader-spinner';
import { IoMdClose, IoMdCloseCircle } from 'react-icons/io';
import InventoryForm from './InventoryForm';
import EditInventory from './EditInventory';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { MdOutlineAutoDelete } from "react-icons/md";
import { api_url } from '../../../config';
import DeleteInventory from './DeleteInventory';
import { GrEdit } from 'react-icons/gr';
import { useLocation } from 'react-router';
import { hasPermission } from '../../../Redux/permissionSlice';
import { selectRoles } from '../../../Redux/AuthSlice';
import exportFromJSON from 'export-from-json';
import Table from '../../../components/dataUi/Table';
import { formatDate } from '../../../utils/dateUtils';
import Portal from '../../../utils/Portal';

const InventoryInfo = () => {
    const [showModal, setShowModal] = useState(false)
    const [editModal, setEditModal] = useState(false)
    const [deleteModal, setDeleteModal] = useState(false)
    const [inventories, setInventories] = useState([])
    const [filteredInventories, setFilteredInventories] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedInventory, setSelectedInventory] = useState()
    const [loading, setLoading] = useState(true)
    const [selectedDropdown, setSelectedDropdown] = useState(null);
    const [sortby, setSortby] = useState('-created_at')

    const location = useLocation();

    const getPath = (specificPath) => {
        return location.pathname === specificPath;
    };
    const isRender = getPath('/wholesale/dashboard');

    const modalRef = useRef()
    const popUpRef = useRef()

    useEffect(() => {
        // Function to handle click outside of modal
        const handleClickOutside = (event) => {
            if (popUpRef.current && !popUpRef.current.contains(event.target)) {
                setSelectedDropdown(null);
            }
        };

        // Attach the event listener
        document.addEventListener('mousedown', handleClickOutside);

        // Cleanup the event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const showEditModal = (event, inventory) => {
        if (event) {
            event.preventDefault(); // Prevent default behavior
            event.stopPropagation();
        }
        setEditModal(!editModal)
        setSelectedInventory(inventory)
    }

    const showDeleteModal = (event, inventory) => {
        if (event) {
            event.preventDefault(); // Prevent default behavior
            event.stopPropagation();
        }
        setDeleteModal(!deleteModal)
        setSelectedInventory(inventory)
    }

    const toggleInventoryForm = () => {
        setShowModal(!showModal)
    };

    const { id, branch_id, token } = useSelector((state) => state.auth)
    const selectedRoles = useSelector(selectRoles);
    const roles = Array.isArray(selectedRoles) ? selectedRoles : selectedRoles.split(',').map(role => role.trim());

    const fetchInventories = async () => {
        setLoading(true);
        if (!navigator.onLine) {
            toast("You're offline");
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(api_url + `/api/v1/wholesaler/user/${id}/branch/${branch_id}/inventory`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            })
            const data = response.data.data;
            setInventories(data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchInventories();
        fetchSuppliers();
    }, [id, token, sortby]);

    const [suppliers, setSuppliers] = useState([])
    const fetchSuppliers = async () => {
        try {
            const response = await axios.get(api_url + `/api/v1/wholesaler/user/${id}/branch/${branch_id}/supplier`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            })
            const data = response.data.data
            setSuppliers(data)
        } catch (error) {
            console.log(error);
        }
    }

    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            setShowModal(false);
            setEditModal(false)
            setDeleteModal(false)
        }
    };

    useEffect(() => {
        // Attach the event listener when the modal is open
        if (showModal || editModal || deleteModal) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            // Remove the event listener when the modal is closed
            document.removeEventListener('mousedown', handleClickOutside);
        }
        // Clean up function to remove event listener on unmount
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showModal, editModal, deleteModal]);

    useEffect(() => {
        if (inventories && Array.isArray(inventories)) {
            setFilteredInventories(inventories);
        }
    }, [inventories]);

    const exportFunc = () => {
        const data = inventories?.map(i => ({
            id: i.id,
            Product_name: i.product_name,
            Supplier: i.supplier.name,
            Manufacturer: i.manufacturer,
            Public: i.public_inventory,
            Description: i.description,
            Date_created: new Date(i?.created_at).toLocaleDateString()
        }))
        const fileName = 'Inventories'
        const exportType = exportFromJSON.types.csv

        exportFromJSON({ data, fileName, fields: ["id", "Product_name", "Supplier", "Manufacturer", "Public", "Description", "Date_created"], exportType })
    }

    // Define table headers
    const tableHeaders = [
        { key: 'number', label: 'NO', sortable: false },
        { key: 'product_name', label: 'Product Name', sortable: true },
        { key: 'supplier_name', label: 'Supplier', sortable: true },
        { key: 'manufacturer', label: 'Manufacturer', sortable: true },
        { key: 'public_inventory', label: 'Public', sortable: true },
        { key: 'description', label: 'Description', sortable: false },
        { key: 'created_at', label: 'Date Created', sortable: true },
    ];

    // Define actions for table rows
    const tableActions = !isRender ? [
        {
            label: 'Edit',
            variant: 'secondary',
            icon: <GrEdit className="text-xs" />,
            onClick: (row) => showEditModal(null, row._originalData),
            showIf: () => hasPermission(roles, ['inventoryWriter', 'inventoryAdmin'])
        },
        {
            label: 'Delete',
            variant: 'danger',
            icon: <MdOutlineAutoDelete className="text-xs" />,
            onClick: (row) => showDeleteModal(null, row._originalData),
            showIf: () => hasPermission(roles, ['inventoryAdmin'])
        }
    ] : [];

    // Format inventory data for the Table component
    const formatInventoriesForTable = () => {
        return filteredInventories.map((inventory, index) => ({
            number: index + 1,
            product_name: inventory.product_name,
            supplier_name: inventory.supplier?.name,
            manufacturer: inventory.manufacturer,
            public_inventory: inventory.public_inventory ? 'Yes' : 'No',
            description: inventory.description || '-',
            created_at: formatDate(new Date(inventory.created_at).toLocaleDateString()),
            _originalData: inventory
        }));
    };

    // Define status configuration for styling
    const statusConfig = {
        'Yes': {
            label: 'Yes',
            className: 'bg-green-200 text-green-600 border border-green-400'
        },
        'No': {
            label: 'No',
            className: 'bg-red-200 text-red-600 border border-red-400'
        }
    };

    return (
        <div className='font-lufga'>
            {loading && (
                <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm">
                    <InfinitySpin color="#008DDA" loading={loading} size={40} />
                </div>
            )}

            <div className='mx-6 font-lufga'>
                {!loading && (
                    <Table
                        headers={tableHeaders}
                        rows={formatInventoriesForTable()}
                        actions={tableActions}
                        emptyMessage={searchTerm ? `No results found for "${searchTerm}"` : "No inventories available"}
                        headerBgColor="bg-blue-200"
                        title="Inventories"
                        itemsPerPage={50}
                        searchPlaceholder="Search by ID, product, supplier..."
                        onAdd={!isRender && hasPermission(roles, ['inventoryWriter', 'inventoryAdmin']) ? toggleInventoryForm : undefined}
                        addButtonText="Add Inventory"
                        addButtonClassName="bg-blue-500 hover:bg-blue-600 text-white"
                        exportButtonText="Export"
                        onExport={exportFunc}
                        statusConfig={statusConfig}
                        className="text-xs"
                    />
                )}

                {showModal && (
                    <Portal>
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
                            <div ref={modalRef} className="p-2 bg-white rounded-md shadow-lg min-h-max z-[9999]">
                                <IoMdClose size={24} onClick={toggleInventoryForm} className='transition duration-150 ease-out cursor-pointer hover:scale-105 hover:text-red-800' />
                                <InventoryForm suppliers={suppliers} toggle={toggleInventoryForm} fetchInventories={fetchInventories} />
                            </div>
                        </div>
                    </Portal>
                )}
                {editModal && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50">
                        <div ref={modalRef} className="p-2 bg-white rounded-md shadow-lg min-h-max z-[9999]">
                            <IoMdCloseCircle size={24} onClick={() => setEditModal(false)} className='transition duration-150 ease-out cursor-pointer hover:scale-105 hover:text-red-800' />
                            <EditInventory toggle={() => setEditModal(false)} inventory={selectedInventory} suppliers={suppliers} fetchInventories={fetchInventories} />
                        </div>
                    </div>
                )}
                {deleteModal && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-800 bg-opacity-50">
                        <div ref={modalRef} className="p-2 bg-white rounded-md shadow-lg min-h-max z-[9999]">
                            <IoMdCloseCircle size={24} onClick={() => setDeleteModal(false)} className='transition duration-150 ease-out cursor-pointer hover:scale-105 hover:text-red-800' />
                            <DeleteInventory toggle={() => setDeleteModal(false)} data={selectedInventory} fetchInventories={fetchInventories} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InventoryInfo;
